import React, { ReactNode } from "react";
import { Box, Grid2 as Grid } from "@platform/style";
import { getEnv } from "../utils/getEnv";
import { useGetWithToken } from "@platform/core";
import { useValidContexts, ValidContext } from "./hooks/useValidContexts";

interface HeaderButton {
  key: string;
  requiredContext?: keyof ValidContext | null;
  index: string;
  content: ReactNode;
}

export interface RegisteredComponent {
  Key: string;
  Name: string;
  RequiredContext?: keyof ValidContext | null;
  ComponentType: string;
  Index: string;
  Path: string;
}

export const HeaderButtons = () => {
  const componentRegistrationUrl =
    getEnv().API_BASE_URL + "/st/platform/header/services/v1/components";

  const [registeredComponents] = useGetWithToken<RegisteredComponent[]>(
    componentRegistrationUrl,
    []
  );

  const [registeredButtons, setRegisteredButtons] = React.useState<RegisteredComponent[]>([]);
  const [pageHeaderButtons, setPageHeaderButtons] = React.useState<HeaderButton[]>([]);
  const validContexts = useValidContexts();

  React.useEffect(() => {
    setRegisteredButtons(() =>
      registeredComponents.filter((component) => component.ComponentType === "HeaderButton")
    );
  }, [registeredComponents]);

  React.useEffect(() => {
    registeredButtons.map(async (button) => {
      let contextValid;

      switch (button.RequiredContext) {
        case "loan":
          contextValid = validContexts.loan;
          break;
        case "workItem":
          contextValid = validContexts.workItem;
          break;
        case undefined:
          contextValid = true;
          break;
        default:
          contextValid = false; // we got something unexpected
          break;
      }
      if (contextValid) {
        let importedContent: ReactNode | null = null;

        try {
          importedContent = (await System.import(button.Path)).default();
        } catch (error) {
          reportError(error);
        }

        if (importedContent !== null) {
          setPageHeaderButtons((existingButtons) => {
            if (existingButtons.find((existingButton) => existingButton.key === button.Key)) {
              return existingButtons; // Don't add duplicate buttons
            }
            const newButtons = [
              ...existingButtons,
              {
                key: button.Key,
                content: importedContent,
                index: button.Index,
              },
            ];
            return newButtons.sort((a, b) => a.index.localeCompare(b.index));
          });
        }
      } else {
        setPageHeaderButtons((existingButtons) =>
          existingButtons.filter((existingButton) => existingButton.key !== button.Key)
        );
      }
    });
  }, [registeredButtons, validContexts]);

  function getPageHeaderButtons() {
    return pageHeaderButtons.map((button) => (
      <Box component={"span"} key={button.key}>
        {button.content}
      </Box>
    ));
  }

  return (
    <Grid size={{ xs: "auto" }} sx={{ justifyContent: "flex-end", paddingRight: "10px" }}>
      <Box>{getPageHeaderButtons()}</Box>
    </Grid>
  );
};
